import React from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  useTheme,
  useMediaQuery,
  Breadcrumbs,
  Link as MUILink,
  Divider,
  Paper,
} from "@mui/material";
import {
  MenuBookOutlined,
  ChatBubbleOutline,
  SupportAgent,
} from "@mui/icons-material";
import { useSelector, useDispatch } from "react-redux";
import {
  setTicketForm,
  resetForm,
  createSupportTicket,
} from "../redux/slices/supportSlice";
import { toast } from "react-toastify"; // Optional: for user feedback
import { Link } from "react-router-dom";
import PageTitle from "../Components/PageTitle";

export default function SellerSupport() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch();

  const { subject, description } = useSelector(
    (state) => state.support.ticketForm,
  );
  const loading = useSelector((state) => state.support.loading);
  const error = useSelector((state) => state.support.error);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await dispatch(createSupportTicket({ subject, description })).unwrap();
      toast.success("Support ticket created successfully!");
      dispatch(resetForm());
    } catch (err) {
      toast.error(error || "Failed to create support ticket");
    }
  };

  return (
    <>
    <PageTitle
            title="Help & Support - BizBridge"
            description="Get help and support for your orders, invoices, and account. Create support tickets or find quick solutions to common issues."
            keywords="seller support, help desk, support ticket, order issue, invoice help, customer support"
            url="/seller-dashboard/support"
          />
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        background: "#f8fafc",
        py: { xs: 1, sm: 2 },
      }}
    >
      {/* Breadcrumb */}
      <Breadcrumbs
        aria-label="breadcrumb"
        sx={{
          width: "fit-content",
          backgroundColor: "#fff",
          ml: { xs: 1, sm: 2.9 },
          padding: "13px",
          borderRadius: "12px",
          boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
        }}
      >
        <MUILink
          component={Link}
          to="/seller-dashboard/pending-invoices"
          sx={{ color: "inherit", textDecoration: "none" }}
        >
          Pending Invoices
        </MUILink>
        <Typography sx={{ color: "#0b4064", fontWeight: "bold" }}>
          Help & Support
        </Typography>
      </Breadcrumbs>

      <Divider sx={{ my: 2 }} />
      <Box sx={{ maxWidth: 1350, mx: "auto", px: { xs: 2, sm: 3 } }}>
        <Typography fontWeight={700} fontSize={22} sx={{ mb: 3 }}>
          Help & Support
        </Typography>

        {/* Cards Row */}
        <Grid
          container
          spacing={isMobile ? 2 : 4}
          justifyContent={isMobile ? "center" : "flex-start"}
        >
          {[
            {
              icon: MenuBookOutlined,
              title: "Seller Guide",
              description:
                "Find answers and guides on how to use the platform.",
            },
            {
              icon: ChatBubbleOutline,
              title: "Live Chat",
              description: "Chat with our support team for immediate help.",
            },
            {
              icon: SupportAgent,
              title: "Create Ticket",
              description: "Create a new support ticket for your issue.",
            },
          ].map(({ icon: Icon, title, description }) => (
            <Grid
              item
              xs={12}
              sm={12}
              md={6}
              lg={4}
              key={title}
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: 1,
                  p: { xs: 2, sm: 3 },
                  borderRadius: 3,
                  height: 140,
                  backgroundColor: "#fff",
                  boxShadow: "0 2px 8px rgba(80, 80, 80, 0.04)",
                }}
              >
                <Box>
                  <Icon sx={{ color: "#09324d", fontSize: 35, mr: 1 }} />
                </Box>
                <Typography
                  variant="h6"
                  sx={{ mt: 0.5, fontWeight: "bold", color: "#192233" }}
                >
                  {title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ opacity: 0.9, color: "#567" }}
                >
                  {description}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* Support Ticket Card */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            px: { xs: 2, sm: 3, md: 4 },
            py: { xs: 2, sm: 3 },
            backgroundColor: "#fff",
            boxShadow: "0 2px 18px 0 rgba(95,134,184,0.07)",
            mt: 5,
          }}
        >
          <CardContent sx={{ px: 0, py: 0 }}>
            <Typography
              variant="h6"
              fontWeight={700}
              mb={0.7}
              sx={{ fontSize: { xs: 17, md: 19 }, color: "#222" }}
            >
              Submit a Support Ticket
            </Typography>
            <Typography
              sx={{
                color: "#09324d",
                fontWeight: 500,
                fontSize: 14,
                mb: 2,
              }}
            >
              Describe your issue in detail and our team will get back to you.
            </Typography>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    value={subject}
                    onChange={(e) =>
                      dispatch(setTicketForm({ subject: e.target.value }))
                    }
                    size="small"
                    fullWidth
                    placeholder="Subject (e.g., Payment Issue for PAY003)"
                    variant="outlined"
                    sx={{ bgcolor: "#f5f6f9", borderRadius: 2 }}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    value={description}
                    onChange={(e) =>
                      dispatch(setTicketForm({ description: e.target.value }))
                    }
                    size="small"
                    fullWidth
                    multiline
                    minRows={3}
                    placeholder="Please describe your issue in detail..."
                    variant="outlined"
                    sx={{ bgcolor: "#f5f6f9", borderRadius: 2 }}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    sx={{ borderRadius: 2, mt: 1 }}
                    fullWidth={isMobile}
                    disabled={loading}
                  >
                    {loading ? "Submitting..." : "Submit Ticket"}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </CardContent>
        </Card>
      </Box>
    </Box>
    </>
  );
}
